import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogOut, User, Loader2 } from 'lucide-react';
import { useUserProfile } from '../hooks/useUserProfile';

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { userPhoto, userProfile, loadingPhoto } = useUserProfile();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!user) return null;

  // Use profile data if available, otherwise fall back to user account data
  const displayName = userProfile?.displayName || user.name || 'User';
  const email = userProfile?.mail || userProfile?.userPrincipalName || user.username || user.localAccountId;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            {loadingPhoto ? (
              <AvatarFallback>
                <Loader2 className="h-4 w-4 animate-spin" />
              </AvatarFallback>
            ) : (
              <>
                {userPhoto && <AvatarImage src={userPhoto} alt={displayName} />}
                <AvatarFallback>
                  {displayName ? getInitials(displayName) : <User className="h-4 w-4" />}
                </AvatarFallback>
              </>
            )}
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {displayName}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
